if (!customElements.get('wlsn-infinite-pagination')) {
  class WLSNInfinitePagination extends HTMLElement {
    constructor() {
      super();
      this.observer = null;
      this.isLoading = false;
      this.isComplete = false;

      this.classes = {
        loading: 'loading',
        complete: 'complete',
      };

      this.onButtonClick = this._onButtonClick.bind(this);
    }

    /**
     * Get DOM elements
     * @returns {object} - Object containing DOM element references
     */
    get elements() {
      return {
        trigger: this.querySelector('[data-observe-trigger]'),
        button: this.querySelector('[data-load-more-btn]'),
        loadingIndicator: this.querySelector('[data-loading-indicator]'),
      };
    }

    /**
     * Get data attributes
     * @returns {object} - Object containing data attributes
     */
    get attributes() {
      return {
        currentPage: this.dataset.currentPage,
        totalPages: this.dataset.totalPages,
        nextUrl: this.dataset.nextUrl,
        threshold: this.dataset.threshold,
      };
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      if (!this.hasAttribute('ajax')) return;

      this.currentPage = parseInt(this.attributes.currentPage) || 1;
      this.totalPages = parseInt(this.attributes.totalPages) || 1;
      this.nextUrl = this.attributes.nextUrl || '';
      this.threshold = parseInt(this.attributes.threshold) || 400;

      this._bindEvents();
      this._setupIntersectionObserver();
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      this._unbindEvents();
      this._destroyIntersectionObserver();
    }

    /**
     * Bind event listeners
     * @returns {void}
     */
    _bindEvents() {
      const { button } = this.elements;
      if (button) {
        button.addEventListener('click', this.onButtonClick);
      }
    }

    /**
     * Unbind event listeners
     * @returns {void}
     */
    _unbindEvents() {
      const { button } = this.elements;
      if (button) {
        button.removeEventListener('click', this.onButtonClick);
      }
    }

    /**
     * Setup IntersectionObserver for infinite scroll
     * @returns {void}
     */
    _setupIntersectionObserver() {
      const { trigger } = this.elements;
      if (!trigger || !('IntersectionObserver' in window)) {
        // Fallback: show button only if IntersectionObserver is not supported
        return;
      }

      const options = {
        root: null, // viewport
        rootMargin: `${this.threshold}px`,
        threshold: 0,
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.isLoading && !this.isComplete) {
            this._loadMore();
          }
        });
      }, options);

      this.observer.observe(this.elements.trigger);
    }

    /**
     * Destroy IntersectionObserver
     * @returns {void}
     */
    _destroyIntersectionObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    /**
     * Handle button click event
     * @param {Event} event - The click event
     * @returns {void}
     */
    _onButtonClick(event) {
      event.preventDefault();
      if (!this.isLoading && !this.isComplete) {
        this._loadMore();
      }
    }

    /**
     * Load more content
     * @returns {Promise<void>}
     */
    async _loadMore() {
      if (this.isLoading || this.isComplete) return;

      this.isLoading = true;
      this._setLoadingState();

      try {
        const url = new URL(this.nextUrl, window.location.origin);

        // Trigger event to parent component to load more
        window.WLSNHelpers.triggerEvent(this, 'wlsn:infinite-pagination:load-more', {
          url: url.toString(),
          currentPage: this.currentPage,
          totalPages: this.totalPages,
        });
      } catch (error) {
        console.error('[WLSNInfinitePagination] Error loading more:', error);
        this._removeLoadingState();
        this.isLoading = false;
      }
    }

    /**
     * Update pagination state with new data (called by parent component)
     * @param {number} newPage - The new current page
     * @param {number} newTotalPages - The new total pages
     * @param {string} newNextUrl - The new next page URL
     * @returns {void}
     */
    updatePagination(newPage, newTotalPages, newNextUrl) {
      this.currentPage = newPage;
      this.totalPages = newTotalPages;
      this.nextUrl = newNextUrl;

      // Update data attributes
      this.dataset.currentPage = newPage;
      this.dataset.totalPages = newTotalPages;
      this.dataset.nextUrl = newNextUrl || '';

      // Check if we've reached the last page
      if (!newNextUrl || this.currentPage >= this.totalPages) {
        this.markComplete();
      }

      this._removeLoadingState();
      this.isLoading = false;
    }

    /**
     * Set loading state
     * @returns {void}
     */
    _setLoadingState() {
      const { button, loadingIndicator } = this.elements;
      this.classList.add(this.classes.loading);

      if (button) {
        button.disabled = true;
      }

      if (loadingIndicator) {
        loadingIndicator.setAttribute('aria-hidden', 'false');
      }
    }

    /**
     * Remove loading state
     * @returns {void}
     */
    _removeLoadingState() {
      const { button, loadingIndicator } = this.elements;
      this.classList.remove(this.classes.loading);

      if (button) {
        button.disabled = false;
      }

      if (loadingIndicator) {
        loadingIndicator.setAttribute('aria-hidden', 'true');
      }
    }

    /**
     * Mark pagination as complete (called by parent component)
     * @returns {void}
     */
    markComplete() {
      this.isComplete = true;
      this.classList.add(this.classes.complete);
      this._destroyIntersectionObserver();
    }

    /**
     * Reset pagination state (public method)
     * @returns {void}
     */
    reset() {
      const { trigger } = this.elements;
      this.isLoading = false;
      this.isComplete = false;
      this.classList.remove(this.classes.loading, this.classes.complete);

      if (this.observer && trigger) {
        this._setupIntersectionObserver();
      }
    }
  }

  customElements.define('wlsn-infinite-pagination', WLSNInfinitePagination);
}
